import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  IconButton
} from '@mui/material';
import ServiceTierChangeModal from './ServiceTierChangeModal';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Refresh as RefreshIcon, Info as InfoIcon } from '@mui/icons-material';

function ServiceTierSelect({ onError, disabled }) {
  const { user, refreshUser } = useAuth();
  const [openServiceTierDialog, setOpenServiceTierDialog] = useState(false);
  const [pendingServiceTier, setPendingServiceTier] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);

  // Fetch pricing tiers from the server
  useEffect(() => {
    const fetchPricingTiers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("ServiceTierSelect - fetching pricing tiers");
        const response = await axios.get('/api/pricing-tiers');
        console.log("ServiceTierSelect - pricing tiers response:", response);
        
        if (response.data.status === 'success' && Array.isArray(response.data.data) && response.data.data.length > 0) {
          setPricingTiers(response.data.data);
        } else {
          console.warn("Invalid response format from pricing tiers API:", response.data);
          throw new Error('No pricing tiers available');
        }
      } catch (err) {
        console.error('Error fetching pricing tiers:', err);
        
        // Try fallback endpoint
        try {
          const fallbackResponse = await axios.get('/api/billing/pricing-tiers');
          if (fallbackResponse.data.status === 'success' && Array.isArray(fallbackResponse.data.data) && fallbackResponse.data.data.length > 0) {
            setPricingTiers(fallbackResponse.data.data);
            return;
          } else {
            throw new Error('Fallback endpoint returned invalid data');
          }
        } catch (fallbackErr) {
          console.error('Fallback pricing tiers request also failed:', fallbackErr);
          setError('Unable to retrieve pricing tiers from database');
          setPricingTiers([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPricingTiers();
  }, []);

  // Fetch downgrade eligibility
  useEffect(() => {
    const checkEligibility = async () => {
      if (!user?.serviceTier || user.serviceTier === 'free') return;
      
      try {
        const response = await axios.get('/api/billing/downgrade-eligibility');
        if (response.data.status === 'success') {
          setEligibilityData(response.data.data);
        }
      } catch (err) {
        console.error('Error checking downgrade eligibility:', err);
      }
    };

    checkEligibility();
  }, [user?.serviceTier]);

  const handleTierChange = async (event) => {
    const newTier = event.target.value;
    if (newTier !== user?.serviceTier) {
      setPendingServiceTier(newTier);
      setOpenServiceTierDialog(true);
    }
  };

  // Helper function to check if a tier should be disabled
  const isTierDisabled = (tier) => {
    if (!user?.serviceTier || user.serviceTier === 'free') return false;
    
    const currentTierIndex = pricingTiers.findIndex(t => t.name === user.serviceTier);
    const tierIndex = pricingTiers.findIndex(t => t.name === tier.name);
    const isDowngrade = tierIndex < currentTierIndex;

    return isDowngrade && eligibilityData && !eligibilityData.eligible;
  };

  // Helper function to get tooltip text for disabled tiers
  const getTooltipText = () => {
    if (!eligibilityData?.nextEligibleDate) return '';

    const formattedDate = new Date(eligibilityData.nextEligibleDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `Downgrade available from ${formattedDate}`;
  };

  const handleServiceTierSuccess = async () => {
    try {
      // Create Stripe checkout session for the new tier
      const response = await axios.post('/api/billing/create-checkout-session', {
        newTier: pendingServiceTier,
        successUrl: `${window.location.origin}/dashboard?refresh=true`,
        cancelUrl: `${window.location.origin}/dashboard`
      });

      if (response.data.status === 'success' && response.data.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.data.url;
      } else {
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error:', err);
      onError?.(err.response?.data?.message || err.message || 'Failed to initiate upgrade');
    } finally {
      // Close the dialog
      setOpenServiceTierDialog(false);
    }
  };

  const handleCancelServiceTier = () => {
    setPendingServiceTier(null);
    setOpenServiceTierDialog(false);
  };

  // Function to manually sync user data
  const handleSyncUserData = async () => {
    try {
      setSyncLoading(true);
      const result = await refreshUser();
      if (!result.success) {
        throw new Error(result.error || 'Failed to sync user data');
      }
      console.log('User data synced successfully');
    } catch (err) {
      console.error('Error syncing user data:', err);
      onError?.(err.message || 'Failed to sync user data');
    } finally {
      setSyncLoading(false);
    }
  };

  // Check URL for refresh parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('refresh') === 'true') {
      // Remove the parameter to prevent repeated refreshes
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      
      // Refresh user data
      handleSyncUserData();
    }
  }, []);

  // Helper function to format currency
  const formatCurrency = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="service-tier-label">Service Tier</InputLabel>
          <Select
            labelId="service-tier-label"
            id="serviceTier"
            name="serviceTier"
            value={user?.serviceTier || ''}
            onChange={handleTierChange}
            label="Service Tier"
            disabled={disabled || loading || error || pricingTiers.length === 0}
            sx={{ backgroundColor: 'background.paper' }}
          >
            {loading ? (
              <MenuItem value="" disabled>
                <Box display="flex" alignItems="center">
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography>Loading tiers...</Typography>
                </Box>
              </MenuItem>
            ) : error ? (
              <MenuItem value="" disabled>
                <Typography color="error">Pricing data unavailable</Typography>
              </MenuItem>
            ) : pricingTiers.length === 0 ? (
              <MenuItem value="" disabled>
                <Typography>No pricing tiers available</Typography>
              </MenuItem>
            ) : (
              pricingTiers.map((tier) => {
                const isDisabled = isTierDisabled(tier);
                const tooltipText = isDisabled ? getTooltipText() : '';

                return (
                  <MenuItem 
                    key={tier.name} 
                    value={tier.name}
                    disabled={isDisabled}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                          {tier.displayName || tier.name}
                          {tier.basePrice > 0 && ` - ${formatCurrency(tier.basePrice)}/month`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(tier.monthlyLimit?.toLocaleString() || '0')} API calls/month
                        </Typography>
                      </Box>
                      {isDisabled && (
                        <Tooltip title={tooltipText} placement="right">
                          <span style={{ display: 'flex', cursor: 'help' }}>
                            <InfoIcon 
                              fontSize="small" 
                              color="action" 
                              sx={{ 
                                ml: 1,
                                pointerEvents: 'auto',
                                opacity: 0.7
                              }} 
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </Select>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </FormControl>
        <Tooltip title="Sync tier data">
          <IconButton 
            onClick={handleSyncUserData} 
            disabled={syncLoading}
            sx={{ mt: 1 }}
          >
            {syncLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <ServiceTierChangeModal
        open={openServiceTierDialog}
        onClose={handleCancelServiceTier}
        currentTier={user?.serviceTier}
        newTier={pendingServiceTier}
        onSuccess={handleServiceTierSuccess}
      />
    </>
  );
}

export default ServiceTierSelect; 